import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, ArrowUpRight, Phone } from "lucide-react";
import Logo from "@/components/Logo";
import { SERVICES, servicePagePath, CONTACT } from "@/lib/content";

const NAV = [
  { label: "Blog", href: "/blog" },
  { label: "FAQ", href: "/faq" },
  { label: "Contact", href: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [svcOpen, setSvcOpen] = useState(false);
  const svcTimer = React.useRef(null);
  const openSvc = () => { clearTimeout(svcTimer.current); setSvcOpen(true); };
  const closeSvc = () => { svcTimer.current = setTimeout(() => setSvcOpen(false), 220); };
  const [mSvcOpen, setMSvcOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (href) => {
    setOpen(false); setSvcOpen(false); setMSvcOpen(false);
    navigate(href);
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${scrolled ? "glass py-3" : "py-5 bg-transparent"}`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between">
        <button data-testid="navbar-logo" onClick={() => (window.location.href = "/")} className="flex items-center gap-3 group">
          <Logo height={56} />
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {/* Services mega-menu trigger */}
          <div className="relative" onMouseEnter={openSvc} onMouseLeave={closeSvc}>
            <button
              data-testid="nav-services"
              onMouseEnter={openSvc}
              onClick={() => setSvcOpen((v) => !v)}
              className="flex items-center gap-1 text-[14px] text-white/85 hover:text-white transition-colors"
            >
              Services <ChevronDown className={`w-3.5 h-3.5 transition-transform ${svcOpen ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {svcOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.18 }}
                  className="absolute top-full left-1/2 -translate-x-1/2 pt-4 w-[520px]"
                  data-testid="nav-services-menu"
                >
                  <div className="rounded-2xl bg-[#0e0e12] border border-white/12 shadow-2xl p-3">
                  <div className="grid grid-cols-2 gap-1">
                    {SERVICES.map((s) => (
                      <Link
                        key={s.slug} to={servicePagePath(s.slug)} onClick={() => setSvcOpen(false)}
                        data-testid={`nav-service-${s.slug}`}
                        className="group flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/5 transition-colors"
                      >
                        <span className="text-white text-[14px] flex-1">{s.title}</span>
                        <ArrowUpRight className="w-3.5 h-3.5 text-white/30 group-hover:text-[#1F5EFF] transition-colors" />
                      </Link>
                    ))}
                  </div>
                  <div className="mt-2 pt-2 border-t border-white/10 px-3 py-2 flex items-center justify-between">
                    <span className="text-[11px] text-white/40 uppercase tracking-widest">Not sure what fits?</span>
                    <div className="flex items-center gap-4">
                      <Link to="/services" onClick={() => setSvcOpen(false)} className="text-[13px] text-white/60 hover:text-white transition-colors font-medium" data-testid="nav-all-services">
                        All Services →
                      </Link>
                      <button onClick={() => { setSvcOpen(false); navigate("/book"); }} className="text-[13px] text-[#1F5EFF] hover:text-white transition-colors font-medium" data-testid="nav-quote-book-now">
                        Book Now →
                      </button>
                    </div>
                  </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {NAV.map((n) => (
            <button
              key={n.label} data-testid={`nav-${n.label.toLowerCase().replace(" ", "-")}`}
              onClick={() => goTo(n.href)}
              className="text-[14px] text-white/85 hover:text-white transition-colors relative group"
            >
              {n.label}
              <span className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-0 group-hover:w-full h-px bg-[#8db2ff] transition-all duration-300" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            data-testid="navbar-phone"
            href={`tel:${CONTACT.phone.replace(/[^\d+]/g, "")}`}
            className="hidden xl:inline-flex items-center gap-2 text-[14px] text-white/85 hover:text-white transition-colors"
          >
            <Phone className="w-4 h-4" /> {CONTACT.phone}
          </a>
          <button
            data-testid="navbar-cta-book" onClick={() => navigate("/book")}
            className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1F5EFF] text-white text-sm font-semibold hover:bg-white hover:text-black transition-colors"
          >
            Book Now
          </button>
          <button
            data-testid="navbar-menu-toggle" onClick={() => setOpen((o) => !o)}
            className="lg:hidden p-2 rounded-md text-white/90 hover:text-white hover:bg-white/5" aria-label="Menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            className="lg:hidden glass border-t border-white/10 mt-3 max-h-[85vh] overflow-y-auto"
          >
            <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-2">
              <button
                onClick={() => setMSvcOpen((v) => !v)}
                className="text-left text-white hover:text-white text-lg font-display flex items-center justify-between py-2"
                data-testid="mobile-services-toggle"
              >
                Services <ChevronDown className={`w-4 h-4 transition-transform ${mSvcOpen ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {mSvcOpen && (
                  <motion.div initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }} className="overflow-hidden">
                    <div className="pl-4 border-l border-white/10 space-y-1 pb-2">
                      {SERVICES.map((s) => (
                        <Link key={s.slug} to={servicePagePath(s.slug)} onClick={() => setOpen(false)}
                          className="block py-2 text-white/70 hover:text-white text-base">
                          {s.title}
                        </Link>
                      ))}
                      <Link to="/services" onClick={() => setOpen(false)}
                        data-testid="mobile-all-services"
                        className="block py-2 text-[#1F5EFF] text-base font-medium">
                        All Services →
                      </Link>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              {NAV.map((n) => (
                <button key={n.label} onClick={() => goTo(n.href)}
                  className="text-left text-white/85 hover:text-white text-lg font-display py-2">
                  {n.label}
                </button>
              ))}
              <button onClick={() => { setOpen(false); navigate("/book"); }}
                data-testid="mobile-cta-book"
                className="mt-3 px-5 py-3 rounded-full bg-[#1F5EFF] text-white text-sm font-semibold">
                Book Now
              </button>
              <div className="flex gap-3 mt-2">
                <a href={`tel:${CONTACT.phone.replace(/[^\d+]/g, "")}`} data-testid="mobile-cta-call"
                  className="flex-1 text-center px-5 py-3 rounded-full border border-white/25 text-white text-sm font-medium">
                  Call {CONTACT.phone}
                </a>
                <a href={CONTACT.whatsapp} target="_blank" rel="noreferrer" data-testid="mobile-cta-whatsapp"
                  className="flex-1 text-center px-5 py-3 rounded-full border border-white/25 text-white text-sm font-medium">
                  WhatsApp Us
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
